import { useCallback, useState } from 'react';
import { usePatientAnalyses, useTriggerAnalysis } from '../../../hooks/usePatientAnalysis';
import { cooldownMsFromError } from '../../../services/analysis';
import { useToast } from '../../../providers/ToastProvider';
import { apiErrorMessage } from '../../../utils/apiError';
import { AnalysisEmpty, AnalysisFailed, AnalysisPending } from './AnalysisStates';
import { AnalysisResultView } from './AnalysisResultView';
import type { AnalysisVariant } from './healthConfig';

interface AnalysisTabProps {
    patientId: string;
    /** 'slim': single-column layout for narrow containers (work-queue sidebar). */
    variant?: AnalysisVariant;
}

function formatCooldown(ms: number): string {
    const hours = Math.ceil(ms / (60 * 60 * 1000));
    if (hours <= 1) {
        const minutes = Math.max(1, Math.ceil(ms / (60 * 1000)));
        return `${minutes} minute${minutes === 1 ? '' : 's'}`;
    }
    return `${hours} hours`;
}

function AnalysisTab({ patientId, variant = 'full' }: AnalysisTabProps) {
    const { analyses, latest, isLoading, error, refetch } = usePatientAnalyses(patientId);
    const trigger = useTriggerAnalysis(patientId);
    const { showToast } = useToast();
    // null = follow the latest run; an id = pinned to an older snapshot.
    const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

    const runAnalysis = useCallback(() => {
        trigger.mutate(undefined, {
            onSuccess: () => {
                setSelectedRunId(null);
            },
            onError: (err) => {
                const cooldownMs = cooldownMsFromError(err);
                if (cooldownMs !== null) {
                    showToast(
                        `An analysis ran recently - you can run a new one in ~${formatCooldown(cooldownMs)}.`,
                        'error'
                    );
                    return;
                }
                showToast(apiErrorMessage(err, 'Failed to start the analysis'), 'error');
            }
        });
    }, [trigger, showToast]);

    if (isLoading) {
        if (variant === 'slim') {
            return (
                <div className="flex-1 overflow-auto p-4 flex flex-col gap-3">
                    <div className="h-9 w-56 rounded-xl bg-gray-100 animate-pulse" />
                    <div className="h-28 rounded-xl bg-gray-100 animate-pulse" />
                    <div className="h-32 rounded-xl bg-gray-100 animate-pulse" />
                    <div className="h-40 rounded-xl bg-gray-100 animate-pulse" />
                </div>
            );
        }
        return (
            <div className="flex-1 overflow-auto p-5 flex flex-col gap-4">
                <div className="h-10 w-72 rounded-xl bg-gray-100 animate-pulse" />
                <div className="grid gap-4" style={{ gridTemplateColumns: '236px 1fr' }}>
                    <div className="h-56 rounded-xl bg-gray-100 animate-pulse" />
                    <div className="h-56 rounded-xl bg-gray-100 animate-pulse" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div className="h-48 rounded-xl bg-gray-100 animate-pulse" />
                    <div className="h-48 rounded-xl bg-gray-100 animate-pulse" />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex-1 flex flex-col items-center justify-center gap-3">
                <div className="text-xs text-red-500">Failed to load analyses</div>
                <button
                    onClick={() => refetch()}
                    className="px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                    Retry
                </button>
            </div>
        );
    }

    if (!latest) {
        return (
            <div className="flex-1 overflow-auto flex flex-col">
                <AnalysisEmpty
                    onRun={runAnalysis}
                    isStarting={trigger.isPending}
                    variant={variant}
                />
            </div>
        );
    }

    const displayed = (selectedRunId && analyses.find((a) => a.id === selectedRunId)) || latest;

    if (displayed.status === 'pending') {
        return (
            <div className="flex-1 overflow-auto flex flex-col">
                <AnalysisPending />
            </div>
        );
    }

    if (displayed.status === 'error') {
        return (
            <div className="flex-1 overflow-auto flex flex-col">
                <AnalysisFailed
                    analysis={displayed}
                    onRetry={runAnalysis}
                    isRetrying={trigger.isPending}
                />
            </div>
        );
    }

    return (
        <div className="flex-1 overflow-auto">
            <AnalysisResultView
                analysis={displayed}
                runs={analyses}
                isLatest={displayed.id === latest.id}
                onSelectRun={setSelectedRunId}
                onRerun={runAnalysis}
                isRerunning={trigger.isPending}
                variant={variant}
            />
        </div>
    );
}

export { AnalysisTab };
